import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Collapse,
  IconButton,
  Alert,
  Stack,
  Grid,
} from "@mui/material";
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  CardMembership as CardMembershipIcon,
  FitnessCenter as FitnessCenterIcon,
  CalendarToday as CalendarTodayIcon,
  ShoppingCart as ShoppingCartIcon,
  AllInclusive as AllInclusiveIcon,
  Event as EventIcon,
} from "@mui/icons-material";
import { useAuthStore } from "../../store";
import StoreModal from "../StoreModal";

const AllPassesTable = () => {
  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});

  const { passes } = useAuthStore((state) => ({
    passes: state.passes,
  }));

  const handleOpenStoreModal = () => {
    setStoreModalOpen(true);
  };

  const toggleCardExpansion = (passId) => {
    setExpandedCards((prev) => ({
      ...prev,
      [passId]: !prev[passId],
    }));
  };

  // Calculate pass status
  const getPassStatus = (validUntil) => {
    const now = new Date();
    const expiryDate = new Date(validUntil);
    const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));

    if (daysUntilExpiry < 0) {
      return { status: 'expired', label: 'Wygasł', color: 'error', days: daysUntilExpiry };
    } else if (daysUntilExpiry <= 7) {
      return { status: 'expiring', label: 'Wygasa wkrótce', color: 'warning', days: daysUntilExpiry };
    } else {
      return { status: 'active', label: 'Aktywny', color: 'success', days: daysUntilExpiry };
    }
  };

  // Format expiry date with relative time
  const formatExpiryDate = (validUntil) => {
    const expiryDate = new Date(validUntil);
    const status = getPassStatus(validUntil);
    
    const formattedDate = expiryDate.toLocaleDateString('pl-PL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });

    if (status.days < 0) {
      return `${formattedDate} (wygasł ${Math.abs(status.days)} dni temu)`;
    } else if (status.days === 0) {
      return `${formattedDate} (wygasa dziś!)`;
    } else if (status.days === 1) {
      return `${formattedDate} (wygasa jutro!)`;
    } else if (status.days <= 7) {
      return `${formattedDate} (za ${status.days} dni)`;
    } else {
      return formattedDate;
    }
  };

  // Calculate total remaining lessons
  const getTotalRemainingLessons = (lessonCategories) => {
    if (!lessonCategories || lessonCategories.length === 0) return 0;
    const hasUnlimited = lessonCategories.some(l => l.is_unlimited);
    if (hasUnlimited) return 'unlimited';
    
    return lessonCategories.reduce((sum, lesson) => sum + lesson.remaining_lessons, 0);
  };

  // Format event date
  const formatEventDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('pl-PL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  // Get primary event color (first event's color or default)
  const getPrimaryEventColor = (eventsForPass) => {
    if (!eventsForPass || eventsForPass.length === 0) return '#ff9800';
    return eventsForPass[0].color || '#ff9800';
  };

  // Convert hex color to rgba with alpha
  const hexToRgba = (hex, alpha) => {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result) {
      return `rgba(${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}, ${alpha})`;
    }
    return `rgba(255, 152, 0, ${alpha})`; // fallback to orange
  };

  // Group and sort passes by status
  const groupedPasses = useMemo(() => {
    if (!Array.isArray(passes)) return { active: [], expiring: [], expired: [] };

    const groups = { active: [], expiring: [], expired: [] };
    
    passes.forEach(pass => {
      const status = getPassStatus(pass.valid_until);
      groups[status.status].push(pass);
    });

    return groups;
  }, [passes]);

  // Render a single pass card
  const renderPassCard = (pass, index) => {
    const rawStatus = getPassStatus(pass.valid_until);
    const isExpanded = expandedCards[pass.id] || false;
    const isEventPass = pass.is_event_pass === true;
    const lessonCategories = pass.lesson_categories_for_pass || [];
    const eventsForPass = pass.events_for_pass || [];
    const totalRemaining = isEventPass ? 'event' : getTotalRemainingLessons(lessonCategories);
    const hasMultipleCategories = lessonCategories.length > 1;
    const hasMultipleEvents = eventsForPass.length > 1;
    const primaryEventColor = isEventPass ? getPrimaryEventColor(eventsForPass) : null;
    
    // For event passes, treat "expiring" as "active" (don't show expiring soon warning/status)
    const status = (isEventPass && rawStatus.status === 'expiring') 
      ? { ...rawStatus, status: 'active', label: 'Aktywny', color: 'success' }
      : rawStatus;

    return (
      <Card
        key={index}
        sx={{
          mb: 2,
          borderLeft: 4,
          borderLeftColor: isEventPass ? primaryEventColor : `${status.color}.main`,
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: 6,
            transform: 'translateY(-2px)',
          },
          opacity: status.status === 'expired' ? 0.7 : 1,
        }}
      >
        <CardContent>
          {/* Card Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
              {isEventPass ? (
                <EventIcon sx={{ color: primaryEventColor }} />
              ) : (
                <CardMembershipIcon color="primary" />
              )}
              <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                {pass.pass_type_name}
              </Typography>
              {isEventPass && (
                <Chip 
                  label="Event" 
                  size="small" 
                  sx={{ 
                    ml: 1, 
                    backgroundColor: primaryEventColor,
                    color: '#fff',
                  }} 
                />
              )}
            </Box>
            <Chip
              label={status.label}
              color={status.color}
              size="small"
              icon={status.status === 'active' ? <CheckCircleIcon /> : <WarningIcon />}
            />
          </Box>

          {/* Quick Stats */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarTodayIcon fontSize="small" color="action" />
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Ważny do
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {formatExpiryDate(pass.valid_until)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isEventPass ? (
                  <EventIcon fontSize="small" color="action" />
                ) : (
                  <FitnessCenterIcon fontSize="small" color="action" />
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    {isEventPass ? 'Eventy' : 'Pozostało zajęć'}
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {isEventPass ? (
                      `${eventsForPass.length} ${eventsForPass.length === 1 ? 'event' : 'eventów'}`
                    ) : totalRemaining === 'unlimited' ? (
                      <>
                        <AllInclusiveIcon fontSize="small" />
                        Bez limitu
                      </>
                    ) : (
                      totalRemaining
                    )}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>

          {/* Event Pass - Show events */}
          {isEventPass && eventsForPass.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {/* Events Preview */}
              {hasMultipleEvents && !isExpanded && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {eventsForPass.length} eventów
                </Typography>
              )}

              {/* Expandable Details for events */}
              {hasMultipleEvents && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <IconButton
                    onClick={() => toggleCardExpansion(pass.id)}
                    size="small"
                    sx={{ 
                      color: primaryEventColor,
                      '&:hover': { backgroundColor: hexToRgba(primaryEventColor, 0.1) }
                    }}
                  >
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    <Typography variant="caption" sx={{ ml: 0.5 }}>
                      {isExpanded ? 'Ukryj szczegóły' : 'Pokaż szczegóły'}
                    </Typography>
                  </IconButton>
                </Box>
              )}

              <Collapse in={isExpanded || !hasMultipleEvents}>
                <Box sx={{ mt: 2 }}>
                  {eventsForPass.map((event, idx) => {
                    const eventColor = event.color || '#ff9800';
                    return (
                      <Paper
                        key={idx}
                        elevation={0}
                        sx={{
                          p: 2,
                          mb: idx < eventsForPass.length - 1 ? 1.5 : 0,
                          backgroundColor: hexToRgba(eventColor, 0.1),
                          borderLeft: 3,
                          borderLeftColor: eventColor,
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          {event.event_name || `Event #${event.event_id}`}
                        </Typography>
                        {event.start_date && event.end_date && (
                          <Typography variant="caption" color="text.secondary">
                            {formatEventDate(event.start_date)} - {formatEventDate(event.end_date)}
                          </Typography>
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              </Collapse>
            </Box>
          )}

          {/* Regular Pass - Show lesson categories */}
          {!isEventPass && lessonCategories.length > 0 && (
            <>
              {/* Lesson Categories Preview */}
              {hasMultipleCategories && !isExpanded && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {lessonCategories.length} typów zajęć
                </Typography>
              )}

              {/* Expandable Details */}
              {hasMultipleCategories && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                  <IconButton
                    onClick={() => toggleCardExpansion(pass.id)}
                    size="small"
                    sx={{ 
                      color: 'primary.main',
                      '&:hover': { backgroundColor: 'primary.light', color: 'primary.contrastText' }
                    }}
                  >
                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    <Typography variant="caption" sx={{ ml: 0.5 }}>
                      {isExpanded ? 'Ukryj szczegóły' : 'Pokaż szczegóły'}
                    </Typography>
                  </IconButton>
                </Box>
              )}

              <Collapse in={isExpanded || !hasMultipleCategories}>
                <Box sx={{ mt: 2 }}>
                  {lessonCategories.map((lesson, idx) => {
                    const progress = lesson.is_unlimited 
                      ? 100 
                      : (lesson.consumed_lessons / (lesson.consumed_lessons + lesson.remaining_lessons)) * 100;
                    
                    return (
                      <Paper
                        key={idx}
                        elevation={0}
                        sx={{
                          p: 2,
                          mb: idx < lessonCategories.length - 1 ? 1.5 : 0,
                          backgroundColor: 'grey.50',
                          borderLeft: 3,
                          borderLeftColor: 'primary.light',
                        }}
                      >
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                          {lesson.name}
                        </Typography>
                        
                        {lesson.is_unlimited ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <AllInclusiveIcon color="primary" fontSize="small" />
                            <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                              Bez limitu zajęć
                            </Typography>
                          </Box>
                        ) : (
                          <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                              <Typography variant="caption" color="text.secondary">
                                Wykorzystano: {lesson.consumed_lessons} | Pozostało: {lesson.remaining_lessons}
                              </Typography>
                              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                                {Math.round(progress)}%
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={progress}
                              sx={{
                                height: 8,
                                borderRadius: 4,
                                backgroundColor: 'grey.200',
                                '& .MuiLinearProgress-bar': {
                                  borderRadius: 4,
                                  backgroundColor: lesson.remaining_lessons === 0 ? 'error.main' : 'success.main',
                                },
                              }}
                            />
                          </>
                        )}
                      </Paper>
                    );
                  })}
                </Box>
              </Collapse>
            </>
          )}

          {/* Warning for expiring passes - only for regular passes, not event passes */}
          {status.status === 'expiring' && !isEventPass && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Ten karnet wygasa za {status.days} {status.days === 1 ? 'dzień' : 'dni'}!
            </Alert>
          )}

          {/* Warning for expired passes */}
          {status.status === 'expired' && (
            <Alert severity="error" sx={{ mt: 2 }}>
              Ten karnet wygasł. Kup nowy karnet, aby kontynuować zajęcia.
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  // Empty state
  if (!passes || passes.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CardMembershipIcon sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Nie masz jeszcze żadnych karnetów
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Kup karnet, aby móc zapisywać się na zajęcia
        </Typography>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<ShoppingCartIcon />}
          onClick={handleOpenStoreModal}
        >
          Kup karnet
        </Button>
        <StoreModal
          open={storeModalOpen}
          onClose={() => setStoreModalOpen(false)}
        />
      </Box>
    );
  }

  return (
    <Box>
      {/* Active Passes */}
      {groupedPasses.active.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'success.main' }}>
            Aktywne karnety ({groupedPasses.active.length})
          </Typography>
          <Stack spacing={2}>
            {groupedPasses.active.map((pass, index) => renderPassCard(pass, index))}
          </Stack>
        </Box>
      )}

      {/* Expiring Soon Passes */}
      {groupedPasses.expiring.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'warning.main' }}>
            Karnety wygasające wkrótce ({groupedPasses.expiring.length})
          </Typography>
          <Stack spacing={2}>
            {groupedPasses.expiring.map((pass, index) => renderPassCard(pass, index))}
          </Stack>
        </Box>
      )}

      {/* Expired Passes */}
      {groupedPasses.expired.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: 'error.main' }}>
            Wygasłe karnety ({groupedPasses.expired.length})
          </Typography>
          <Stack spacing={2}>
            {groupedPasses.expired.map((pass, index) => renderPassCard(pass, index))}
          </Stack>
        </Box>
      )}

      {/* Buy Pass Button */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          startIcon={<ShoppingCartIcon />}
          onClick={handleOpenStoreModal}
          sx={{ minWidth: 200 }}
        >
          Kup nowy karnet
        </Button>
      </Box>

      <StoreModal
        open={storeModalOpen}
        onClose={() => setStoreModalOpen(false)}
      />
    </Box>
  );
};

export default AllPassesTable;
